/**
 * Admin V5 — Assignments Panel (Digital SAT · Phase 2B).
 *
 * Tutor/Admin workflow to assign published tests to students. Reuses:
 *   GET    /api/exam/v2/assignments?status=&test_id=&student_email=
 *   POST   /api/exam/v2/assignments                {student_emails:[…]}
 *   PATCH  /api/exam/v2/assignments/{id}
 *   POST   /api/exam/v2/assignments/{id}/cancel
 *   POST   /api/exam/v2/assignments/{id}/duplicate    (reassign)
 *
 * Only PUBLISHED tests can be assigned. Once an assignment is created:
 *   - The student sees it on the DSAT landing page ("My Assignments").
 *   - Resend email + portal notification fire via exam_notifications.py.
 *   - On submit, assigner receives a completion email + portal notification.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, RefreshCcw, X, Copy, Ban, Save, Users, Send, Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const STATUS_COLORS = {
  assigned:    'bg-amber-100 text-amber-900',
  in_progress: 'bg-blue-100 text-blue-800',
  submitted:   'bg-emerald-100 text-emerald-800',
  expired:     'bg-gray-200 text-gray-700',
  cancelled:   'bg-rose-100 text-rose-800',
};

export default function AdminAssignmentsPanel() {
  const [tab, setTab] = useState('list');                 // 'list' | 'new'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');   // '' = all
  const [tests, setTests] = useState([]);
  const [students, setStudents] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `/api/exam/v2/assignments?status=${statusFilter}` : `/api/exam/v2/assignments`;
      const r = await ax.get(url);
      setItems(r.data?.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    // Reuse published tests + students catalogue for the new-assignment form
    (async () => {
      try {
        const [t, s] = await Promise.all([
          ax.get('/api/exam/v2/tests?exam_type=dsat&status=published&limit=500'),
          ax.get('/api/admin/v5/students?limit=500').catch(() => ({ data: { items: [] } })),
        ]);
        setTests(t.data?.items || []);
        const rawStudents = s.data?.items || s.data?.students || s.data || [];
        setStudents(Array.isArray(rawStudents) ? rawStudents : []);
      } catch { /* silent */ }
    })();
  }, []);

  const cancel = async (a) => {
    if (!window.confirm(`Cancel assignment "${a.test_title}" for ${a.student_email}?`)) return;
    try {
      await ax.post(`/api/exam/v2/assignments/${a.assignment_id}/cancel`);
      toast.success('Assignment cancelled');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
  };

  const reassign = async (a) => {
    if (!window.confirm(`Reassign "${a.test_title}" to ${a.student_email}?`)) return;
    try {
      await ax.post(`/api/exam/v2/assignments/${a.assignment_id}/duplicate`);
      toast.success('Reassigned');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reassign failed'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-assignments-panel">
      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
          <div>
            <div className="text-lg font-bold text-gray-900">Test Assignments</div>
            <div className="text-xs text-gray-600 mt-0.5">
              Assign published tests to students · Reuses the Winhouse Exam Engine + Resend email pipeline.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select className="border rounded px-2 py-1.5 text-sm"
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="asg-status-filter">
              <option value="">All statuses</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In progress</option>
              <option value="submitted">Submitted</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <Button size="sm" variant="outline" onClick={refresh} data-testid="asg-refresh">
              <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setTab(tab === 'new' ? 'list' : 'new')}
              className="bg-red-700 hover:bg-red-800 text-white" data-testid="asg-new">
              {tab === 'new' ? <><X className="h-4 w-4 mr-1" /> Close</> : <><Plus className="h-4 w-4 mr-1" /> New assignment</>}
            </Button>
          </div>
        </div>
      </Card>

      {tab === 'new' && (
        <NewAssignmentForm
          tests={tests}
          students={students}
          onCreated={() => { toast.success('Assignment created'); refresh(); setTab('list'); }}
        />
      )}

      <Card className="p-0 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm" data-testid="asg-empty">
            No assignments in this filter.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Student</th>
                <th className="text-left px-3 py-2">Test</th>
                <th className="text-left px-3 py-2">Assigned by</th>
                <th className="text-left px-3 py-2">Due</th>
                <th className="text-left px-3 py-2">Mode</th>
                <th className="text-left px-3 py-2">Attempts</th>
                <th className="text-left px-3 py-2">Best score</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.assignment_id} className="border-t" data-testid={`asg-row-${a.assignment_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-gray-900">{a.student_name || a.student_email}</div>
                    <div className="text-[10px] text-gray-500">{a.student_email}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    <div className="font-semibold">{a.test_title}</div>
                    <div className="text-[10px] text-gray-500 capitalize">{(a.test_type || '').replace('_', ' ')}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-700 text-[11px]">
                    <div>{a.assigned_by_name || a.assigned_by}</div>
                    <div className="text-[10px] text-gray-500 capitalize">{a.assigned_by_role}</div>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-600">
                    {a.due_at ? new Date(a.due_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-700 uppercase">{a.mode}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-700">{a.attempts_used || 0} / {a.attempt_limit}</td>
                  <td className="px-3 py-2 text-[11px]">
                    {a.best_score_pct != null ? <b className="text-emerald-700">{a.best_score_pct}%</b> : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-700'}>
                      {(a.status || '').replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {['assigned', 'in_progress'].includes(a.status) && (
                      <Button size="sm" variant="outline" className="mr-1" onClick={() => cancel(a)} data-testid={`asg-cancel-${a.assignment_id}`}>
                        <Ban className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {['submitted', 'expired', 'cancelled'].includes(a.status) && (
                      <Button size="sm" variant="outline" onClick={() => reassign(a)} data-testid={`asg-reassign-${a.assignment_id}`}>
                        <Copy className="h-3.5 w-3.5 mr-1" /> Reassign
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}


function NewAssignmentForm({ tests, students, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    test_id: '',
    student_query: '',
    student_emails: [],       // array of email strings
    mode: 'timed',
    due_at: '',
    start_at: '',
    attempt_limit: 1,
    auto_close: true,
    instructions: '',
    notify: true,
  });

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const filteredStudents = useMemo(() => {
    const q = (form.student_query || '').toLowerCase().trim();
    if (!q) return students.slice(0, 30);
    return students.filter((s) =>
      [s.email, s.name, s.full_name].some((v) => (v || '').toLowerCase().includes(q))
    ).slice(0, 30);
  }, [students, form.student_query]);

  const toggleStudent = (email) => {
    const em = (email || '').toLowerCase();
    if (!em) return;
    setF({
      student_emails: form.student_emails.includes(em)
        ? form.student_emails.filter((x) => x !== em)
        : [...form.student_emails, em],
    });
  };

  const submit = async () => {
    if (!form.test_id) { toast.error('Choose a test'); return; }
    if (form.student_emails.length === 0) { toast.error('Select at least one student'); return; }
    setSaving(true);
    try {
      const body = {
        test_id: form.test_id,
        student_emails: form.student_emails,
        mode: form.mode,
        attempt_limit: parseInt(form.attempt_limit || '1', 10),
        auto_close: !!form.auto_close,
        instructions: form.instructions || null,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
        notify: !!form.notify,
      };
      const r = await ax.post('/api/exam/v2/assignments', body);
      const cnt = r.data?.created_count ?? 0;
      const skip = r.data?.skipped_count ?? 0;
      if (cnt > 0) onCreated();
      if (skip > 0) toast.warning(`${skip} student(s) already had an open assignment — skipped.`);
      if (cnt === 0) toast.error('No new assignments created (all skipped as duplicates).');
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="asg-new-form">
      <div className="text-sm font-bold text-gray-900">Assign a test to students</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Test *</Label>
          <select className="border rounded px-2 py-1.5 text-sm w-full h-9"
            value={form.test_id} onChange={(e) => setF({ test_id: e.target.value })}
            data-testid="asg-test-select">
            <option value="">Choose a published test…</option>
            {tests.map((t) => (
              <option key={t.test_id} value={t.test_id}>
                [{(t.test_type || '').replace('_', ' ')}] {t.title}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Mode</Label>
          <select className="border rounded px-2 py-1.5 text-sm w-full h-9"
            value={form.mode} onChange={(e) => setF({ mode: e.target.value })}
            data-testid="asg-mode">
            <option value="timed">Timed</option>
            <option value="untimed">Untimed</option>
            <option value="student_choice">Student chooses</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Start at (optional — leave blank to open now)</Label>
          <Input type="datetime-local" value={form.start_at}
            onChange={(e) => setF({ start_at: e.target.value })}
            data-testid="asg-start" />
        </div>
        <div>
          <Label className="text-xs">Due at (optional)</Label>
          <Input type="datetime-local" value={form.due_at}
            onChange={(e) => setF({ due_at: e.target.value })}
            data-testid="asg-due" />
        </div>
        <div>
          <Label className="text-xs">Attempts allowed</Label>
          <Input type="number" min={1} max={20} value={form.attempt_limit}
            onChange={(e) => setF({ attempt_limit: e.target.value })}
            data-testid="asg-attempts" />
        </div>
        <div className="flex items-center gap-3 pt-6">
          <label className="text-xs flex items-center gap-1">
            <input type="checkbox" checked={form.auto_close}
              onChange={(e) => setF({ auto_close: e.target.checked })} />
            Auto-close past due
          </label>
          <label className="text-xs flex items-center gap-1">
            <input type="checkbox" checked={form.notify}
              onChange={(e) => setF({ notify: e.target.checked })} />
            Notify student (email + portal)
          </label>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Instructions (visible to student)</Label>
          <Textarea rows={2} value={form.instructions}
            onChange={(e) => setF({ instructions: e.target.value })}
            placeholder="Focus on Algebra module 1 first. Submit before Friday."
            data-testid="asg-instructions" />
        </div>
      </div>

      <div>
        <Label className="text-xs">Students * (search then click to select)</Label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Filter className="h-3.5 w-3.5 absolute left-2 top-2.5 text-gray-400" />
            <Input value={form.student_query}
              onChange={(e) => setF({ student_query: e.target.value })}
              placeholder="Search by name or email"
              className="pl-7 h-9 text-xs"
              data-testid="asg-student-search" />
          </div>
          <Users className="h-4 w-4 text-gray-400" />
          <span className="text-[11px] text-gray-500" data-testid="asg-selected-count">
            {form.student_emails.length} selected
          </span>
        </div>
        <div className="border rounded max-h-60 overflow-y-auto mt-1" data-testid="asg-student-list">
          {filteredStudents.length === 0 && (
            <div className="p-3 text-xs text-gray-500">No matching students. Try a different search.</div>
          )}
          {filteredStudents.map((s) => {
            const email = (s.email || '').toLowerCase();
            const selected = form.student_emails.includes(email);
            return (
              <button key={email} type="button"
                onClick={() => toggleStudent(email)}
                className={`w-full text-left px-3 py-1.5 border-b last:border-0 flex items-center gap-2 text-xs hover:bg-gray-50 ${selected ? 'bg-emerald-50' : ''}`}
                data-testid={`asg-student-${email}`}>
                <input type="checkbox" checked={selected} readOnly />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{s.name || s.full_name || email}</div>
                  <div className="text-[10px] text-gray-500">{email}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={submit} disabled={saving}
          className="bg-red-700 hover:bg-red-800 text-white" data-testid="asg-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
          Create assignment{form.student_emails.length > 1 ? 's' : ''}
        </Button>
      </div>
    </Card>
  );
}
